import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Users, Plus, Pencil, Trash2, Search, Building2, FileText, Package, Euro, ArrowLeft, Eye } from "lucide-react";
import { showError } from "../lib/errorHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [guides, setGuides] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewingClient, setViewingClient] = useState(null);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ name: "", nif: "", address: "", city: "", postal_code: "", phone: "", email: "", contact_person: "", notes: "" });

  const fetchData = useCallback(async () => {
    try {
      const [clientsRes, guidesRes, ordersRes] = await Promise.all([
        axios.get(`${API}/clients`),
        axios.get(`${API}/sales-guides`),
        axios.get(`${API}/orders`),
      ]);
      setClients(clientsRes.data);
      setGuides(guidesRes.data);
      setOrders(ordersRes.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", nif: "", address: "", city: "", postal_code: "", phone: "", email: "", contact_person: "", notes: "" });
    setShowForm(true);
  };

  const openEdit = (client) => {
    setEditing(client);
    setForm({ name: client.name, nif: client.nif || "", address: client.address || "", city: client.city || "", postal_code: client.postal_code || "", phone: client.phone || "", email: client.email || "", contact_person: client.contact_person || "", notes: client.notes || "" });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Nome obrigatorio"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/clients/${editing.id}`, form);
        toast.success("Cliente atualizado!");
      } else {
        await axios.post(`${API}/clients`, form);
        toast.success("Cliente criado!");
      }
      setShowForm(false);
      fetchData();
    } catch (e) {
      showError(e, "Erro ao guardar cliente");
    }
  };

  const deleteClient = async (id, name) => {
    if (!window.confirm(`Tem a certeza que quer eliminar o cliente "${name}"?`)) return;
    try {
      await axios.delete(`${API}/clients/${id}`);
      toast.success("Cliente eliminado");
      fetchData();
    } catch (e) { showError(e, "Erro ao eliminar"); }
  };

  const filtered = search ? clients.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || (c.nif && c.nif.includes(search)) || (c.city && c.city.toLowerCase().includes(search.toLowerCase()))) : clients;

  // Client details
  const getClientGuides = (clientId, clientName) => guides.filter(g => g.client_id === clientId || g.client_name === clientName);
  const getClientOrders = (clientId, clientName) => orders.filter(o => o.client_id === clientId || o.client_name === clientName);

  // Viewing client page
  if (viewingClient) {
    const cGuides = getClientGuides(viewingClient.id, viewingClient.name);
    const cOrders = getClientOrders(viewingClient.id, viewingClient.name);
    const totalSales = cGuides.filter(g => g.status === "emitida").reduce((s, g) => s + (g.total_value_with_iva || g.total_value || 0), 0);
    const totalOrders = cOrders.length;

    return (
      <div className="space-y-6" data-testid="client-detail-page">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => setViewingClient(null)}><ArrowLeft className="h-4 w-4 mr-2" />Voltar</Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{viewingClient.name}</h1>
            <p className="text-gray-500 mt-1">Ficha de cliente</p>
          </div>
        </div>

        {/* Client info */}
        <div className="grid grid-cols-4 gap-4">
          <Card><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase">NIF</p><p className="text-lg font-bold">{viewingClient.nif || "-"}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase">Total Vendas</p><p className="text-lg font-bold text-green-600">{totalSales.toFixed(2)} &euro;</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase">Guias Emitidas</p><p className="text-lg font-bold text-blue-600">{cGuides.length}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase">Ordens Producao</p><p className="text-lg font-bold text-purple-600">{totalOrders}</p></CardContent></Card>
        </div>

        {/* Contact details */}
        <Card>
          <CardHeader><CardTitle>Dados do Cliente</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div><span className="text-gray-500">Morada:</span><br /><strong>{viewingClient.address || "-"}</strong></div>
              <div><span className="text-gray-500">Cidade:</span><br /><strong>{viewingClient.city || "-"} {viewingClient.postal_code || ""}</strong></div>
              <div><span className="text-gray-500">Telefone:</span><br /><strong>{viewingClient.phone || "-"}</strong></div>
              <div><span className="text-gray-500">Email:</span><br /><strong>{viewingClient.email || "-"}</strong></div>
              <div><span className="text-gray-500">Pessoa de Contacto:</span><br /><strong>{viewingClient.contact_person || "-"}</strong></div>
              <div><span className="text-gray-500">Notas:</span><br /><strong>{viewingClient.notes || "-"}</strong></div>
            </div>
          </CardContent>
        </Card>

        {/* Sales guides history */}
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" />Historico de Guias de Venda ({cGuides.length})</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Numero</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Artigos</TableHead>
                  <TableHead>Subtotal</TableHead>
                  <TableHead>IVA</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Pagamento</TableHead>
                  <TableHead>Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cGuides.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center text-gray-400 py-6">Sem guias de venda</TableCell></TableRow>
                ) : cGuides.map(g => (
                  <TableRow key={g.id}>
                    <TableCell className="font-mono font-bold">{g.number}</TableCell>
                    <TableCell>{g.created_at ? new Date(g.created_at).toLocaleDateString("pt-PT") : "-"}</TableCell>
                    <TableCell>{g.items?.map(i => `${i.part_code} (x${i.quantity})`).join(", ")}</TableCell>
                    <TableCell>{(g.subtotal || g.total_value || 0).toFixed(2)} &euro;</TableCell>
                    <TableCell>{(g.iva_value || 0).toFixed(2)} &euro;</TableCell>
                    <TableCell className="font-bold">{(g.total_value_with_iva || g.total_value || 0).toFixed(2)} &euro;</TableCell>
                    <TableCell className="text-xs">{g.payment_method || "-"}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${g.status === 'emitida' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {g.status === 'emitida' ? 'Emitida' : 'Anulada'}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
                {cGuides.length > 0 && (
                  <TableRow className="bg-slate-50 font-bold">
                    <TableCell colSpan={5}>TOTAL</TableCell>
                    <TableCell>{totalSales.toFixed(2)} &euro;</TableCell>
                    <TableCell colSpan={2}></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Orders history */}
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Package className="h-5 w-5" />Ordens de Producao ({cOrders.length})</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Codigo</TableHead>
                  <TableHead>Peca</TableHead>
                  <TableHead>Quantidade</TableHead>
                  <TableHead>Produzido</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Data Inicio</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cOrders.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-6">Sem ordens de producao</TableCell></TableRow>
                ) : cOrders.map(o => (
                  <TableRow key={o.id}>
                    <TableCell className="font-mono font-bold">{o.code}</TableCell>
                    <TableCell>{o.part_name || "-"}</TableCell>
                    <TableCell>{o.total_quantity || o.good_quantity_target || 0}</TableCell>
                    <TableCell>{o.good_quantity || 0}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${o.status === 'completed' ? 'bg-green-100 text-green-700' : o.status === 'in_progress' || o.status === 'running' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                        {o.status === 'completed' ? 'Concluida' : o.status === 'in_progress' || o.status === 'running' ? 'Em Producao' : o.status === 'delivered' ? 'Entregue' : 'Pendente'}
                      </span>
                    </TableCell>
                    <TableCell>{o.planned_start ? new Date(o.planned_start).toLocaleDateString("pt-PT") : "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Client list page
  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Clientes</h1>
          <p className="text-gray-500 mt-1">Base de dados de clientes</p>
        </div>
        <Button onClick={openNew} data-testid="new-client-btn">
          <Plus className="h-4 w-4 mr-2" /> Novo Cliente
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        <input className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm" placeholder="Pesquisar por nome, NIF ou cidade..." value={search} onChange={e => setSearch(e.target.value)} data-testid="client-search" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" /> Clientes ({filtered.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>NIF</TableHead>
                <TableHead>Cidade</TableHead>
                <TableHead>Telefone</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Guias</TableHead>
                <TableHead>Total Vendas</TableHead>
                <TableHead>Acoes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center text-gray-400 py-8">Nenhum cliente encontrado</TableCell></TableRow>
              ) : filtered.map(c => {
                const cg = getClientGuides(c.id, c.name);
                const total = cg.filter(g => g.status === "emitida").reduce((s, g) => s + (g.total_value_with_iva || g.total_value || 0), 0);
                return (
                  <TableRow key={c.id}>
                    <TableCell className="font-bold">{c.name}</TableCell>
                    <TableCell className="font-mono">{c.nif || "-"}</TableCell>
                    <TableCell>{c.city || "-"}</TableCell>
                    <TableCell>{c.phone || "-"}</TableCell>
                    <TableCell className="text-sm">{c.email || "-"}</TableCell>
                    <TableCell className="text-center font-medium">{cg.length}</TableCell>
                    <TableCell className="font-bold text-green-600">{total > 0 ? `${total.toFixed(2)} \u20AC` : "-"}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setViewingClient(c)} title="Ver ficha"><Eye className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(c)} title="Editar"><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteClient(c.id, c.name)} className="text-red-500" title="Eliminar"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" /> {editing ? "Editar Cliente" : "Novo Cliente"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="col-span-2"><label className="text-sm font-semibold text-gray-700">Nome *</label><input className="w-full mt-1 border rounded-lg px-3 py-2 text-sm" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nome do cliente ou empresa" data-testid="client-name-input" /></div>
            <div><label className="text-sm font-semibold text-gray-700">NIF</label><input className="w-full mt-1 border rounded-lg px-3 py-2 text-sm" value={form.nif} onChange={e => setForm({ ...form, nif: e.target.value })} placeholder="Contribuinte" data-testid="client-nif-input" /></div>
            <div><label className="text-sm font-semibold text-gray-700">Telefone</label><input className="w-full mt-1 border rounded-lg px-3 py-2 text-sm" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Telefone" /></div>
            <div className="col-span-2"><label className="text-sm font-semibold text-gray-700">Morada</label><input className="w-full mt-1 border rounded-lg px-3 py-2 text-sm" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Rua, numero, etc." /></div>
            <div><label className="text-sm font-semibold text-gray-700">Cidade</label><input className="w-full mt-1 border rounded-lg px-3 py-2 text-sm" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="Cidade" /></div>
            <div><label className="text-sm font-semibold text-gray-700">Codigo Postal</label><input className="w-full mt-1 border rounded-lg px-3 py-2 text-sm" value={form.postal_code} onChange={e => setForm({ ...form, postal_code: e.target.value })} placeholder="0000-000" /></div>
            <div><label className="text-sm font-semibold text-gray-700">Email</label><input className="w-full mt-1 border rounded-lg px-3 py-2 text-sm" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@empresa.pt" /></div>
            <div><label className="text-sm font-semibold text-gray-700">Pessoa de Contacto</label><input className="w-full mt-1 border rounded-lg px-3 py-2 text-sm" value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} placeholder="Nome do contacto" /></div>
            <div className="col-span-2"><label className="text-sm font-semibold text-gray-700">Notas</label><textarea className="w-full mt-1 border rounded-lg px-3 py-2 text-sm" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Observacoes..." /></div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t mt-4">
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={submit} data-testid="save-client-btn">{editing ? "Guardar Alteracoes" : "Criar Cliente"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
